import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { useSessionStore } from '../auth/session-store';
import { AcceptInvitationPage } from '../auth/accept-invitation-page';
import { ForgotPasswordPage } from '../auth/forgot-password-page';
import { ResetPasswordPage } from '../auth/reset-password-page';
import { LoginPage } from '../auth/login-page';
import { AppShell } from '../shell/app-shell';
import { PortalDeliveryNotesPage } from './delivery-notes/portal-delivery-notes-page';
import { HelpPage } from '../help/help-page';
import { HomePage } from '../orders/home-page';
import { OrderDetailPage } from '../orders/order-detail-page';
import { OrderFormPage } from '../orders/order-form-page';
import { OrderListPage } from '../orders/order-list-page';
import { TemplateListPage } from '../templates/template-list-page';

const rootRoute = createRootRoute();

const loginSearchSchema = z.object({
  reset: z.literal('success').optional().catch(undefined),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

/** Reached from the recovery email link, no session yet. */
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: resetPasswordSearchSchema,
  component: ResetPasswordPage,
});

const acceptInvitationSearchSchema = z.object({
  token: z.string().optional().catch(undefined),
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation',
  validateSearch: acceptInvitationSearchSchema,
  component: AcceptInvitationPage,
});

/**
 * Everything below this route requires a session. `client-portal/design.md`
 * §6.3: `/` is not a real screen — the customer's next order is the home.
 *
 * This `beforeLoad` covers the first load only: TanStack Router only
 * re-runs it on navigation, never on a store change while already on a
 * matched route. The mid-session case (a 401 that survives while the user
 * sits on a page) is `ProtectedRoute`'s job, inside `AppShell` — it
 * subscribes to the session reactively instead.
 */
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: () => {
    const { isAuthenticated, isRestoring } = useSessionStore.getState();

    if (!isRestoring && !isAuthenticated()) {
      // TanStack Router signals a redirect by throwing its own marker
      // object, which is how it unwinds out of a loader.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  component: AppShell,
});

const ordersHomeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders',
  component: HomePage,
});

/**
 * Declared before `orderEditRoute` so the static segment `new` never
 * gets matched as an `$orderId`, same resolution criterion `apps/
 * web-admin/src/routes/router.tsx` already documents for its own
 * `/customers/new` vs. `/customers/$customerId`.
 */
const orderNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/new',
  component: OrderFormPage,
});

const orderEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/$orderId/edit',
  component: OrderFormPage,
});

/**
 * Declared before `orderDetailRoute` for the same reason `orderNewRoute`
 * is declared before `orderEditRoute`: `history`/`templates` are static
 * segments that must never be swallowed by `$orderId`.
 */
const orderHistoryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/history',
  component: OrderListPage,
});

const orderTemplatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/templates',
  component: TemplateListPage,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/$orderId',
  component: OrderDetailPage,
});

const helpRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/help',
  component: HelpPage,
});

/** `design.md` §10: `/portal/delivery-notes`, unlike every other portal route, keeps the literal `portal` segment. */
const deliveryNotesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/portal/delivery-notes',
  component: PortalDeliveryNotesPage,
});

const rootRedirectRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/orders' });
  },
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  acceptInvitationRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    rootRedirectRoute,
    ordersHomeRoute,
    orderNewRoute,
    orderHistoryRoute,
    orderTemplatesRoute,
    orderEditRoute,
    orderDetailRoute,
    deliveryNotesRoute,
    helpRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
